import { GridFilterItem, GridSortItem } from '@mui/x-data-grid-pro';

import { QueryOption } from '../@types';
import { httpClient, HttpMethod } from './http-client';
import {
  BulkUpdateRegionsRequest,
  BulkUpdateRegionsResponse,
  CreateRegionRequest,
  CreateRegionResponse,
  RebuildNodeZonesResponse,
  Region,
  RegionListResponse,
  RenameZoneRequest,
  RenameZoneResponse,
  UpdateRegionRequest,
  UpdateRegionResponse,
  ZoneNodeCountResponse,
  ZoneServiceCountResponse,
  ZonesResponse,
  ZoneSummaryResponse,
  ZoneUserCountResponse,
} from '../@types/geosocial.type';
import {
  StructureStatesResponse,
  StatesWithCountryResponse,
  RegionSuburbsResponse,
} from '../@types/structure.type';
import { isNotArrayEmpty } from '../utils/array-utils';
import { mapNumericEqualsOperatorForWire } from '../utils/filterModel';

/** Region list filter fields backed by *integer* columns on `geosocial.region`.
 *  Their `equals` must go out as `=`: the list read goes through lib-framework
 *  `getSequelizeOperator`, which maps `equals` to `Op.iLike`, and
 *  `integer ILIKE text` is invalid in PostgreSQL. See
 *  mapNumericEqualsOperatorForWire. `isActive` is not listed - the boolean
 *  column is filtered with `is`, which already maps to `Op.eq`. */
export const REGION_NUMERIC_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'id',
  'suburbId',
  'cityId',
  'stateId',
  'countryId',
]);

export const getStates = (): Promise<StructureStatesResponse[]> => {
  return httpClient<StructureStatesResponse[]>('/states', HttpMethod.Get);
};

export const getStateWithCountryData = (stateId: number): Promise<StatesWithCountryResponse> => {
  return httpClient<StatesWithCountryResponse>(`/states/${stateId}/country`, HttpMethod.Get);
};

export const getZones = (): Promise<ZonesResponse[]> => {
  return httpClient<ZonesResponse[]>('/zones', HttpMethod.Get);
};

export const getSuburbsByPostcode = (postcode: string): Promise<RegionSuburbsResponse[]> => {
  return httpClient<RegionSuburbsResponse[]>(`/suburbs/postcode/${postcode}`, HttpMethod.Get);
};

/**
 * Query values go through URLSearchParams rather than string interpolation.
 * Zone names and suburbs are free text, so a value containing `&`, `=` or `#`
 * would otherwise break out of its parameter and inject others into the query.
 * The backend still receives `field=operator,value` once Express decodes it.
 */
export const getRegionsList = (queryOptions: QueryOption): Promise<RegionListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  const params = new URLSearchParams({
    limit: String(pageSize),
    page: String(page + 1),
  });

  if (sortModel && isNotArrayEmpty(sortModel as GridSortItem[], 0)) {
    params.append('order_by', String(sortModel[0]?.field ?? ''));
    params.append('order_direction', String(sortModel[0]?.sort ?? ''));
  }

  filterModel?.items?.forEach((filter: GridFilterItem) => {
    const operator = mapNumericEqualsOperatorForWire(
      filter?.field,
      filter?.operator,
      REGION_NUMERIC_FILTER_FIELDS
    );
    const value = filter?.value ?? '';
    if (!filter?.field) return;
    params.append(filter.field, `${operator},${value}`);
  });

  return httpClient<RegionListResponse>(`/regions/list?${params.toString()}`, HttpMethod.Get);
};

export const getRegionsByZone = (geoName: string): Promise<Region[]> => {
  return httpClient<Region[]>(`/regions/byZone/${encodeURIComponent(geoName)}`, HttpMethod.Get);
};

/**
 * Returns `null` for an unknown id rather than rejecting: `remoteCall` in
 * lib-framework turns the 404 from `api-geosocial` into an empty 200 body, so
 * the BFF answers `200 null`. Callers must null-check rather than rely on an
 * error state.
 */
export const getRegionById = (id: number): Promise<Region | null> => {
  return httpClient<Region | null>(`/regions/${id}`, HttpMethod.Get);
};

/**
 * The BFF replies `200` (not `201`) because it forwards the proxy result with a
 * bare `res.send`. A duplicate region violates the `region_ukey` constraint on
 * (`postCode`, `locality`, `state`, `country`) and surfaces as a generic
 * `500 { message: 'Failed to create region' }`, as does every validation
 * failure, so error handling cannot yet tell the two apart.
 */
export const createRegion = (region: CreateRegionRequest): Promise<CreateRegionResponse> => {
  return httpClient<CreateRegionResponse>('/regions', HttpMethod.Post, region);
};

export const updateRegion = (
  id: number,
  region: UpdateRegionRequest
): Promise<UpdateRegionResponse> => {
  return httpClient<UpdateRegionResponse>(`/regions/${id}`, HttpMethod.Put, region);
};

export const bulkUpdateRegions = (
  payload: BulkUpdateRegionsRequest
): Promise<BulkUpdateRegionsResponse> => {
  return httpClient<BulkUpdateRegionsResponse>('/regions/bulkUpdate', HttpMethod.Post, payload);
};

export const getZoneNodeCount = (geoName: string): Promise<ZoneNodeCountResponse> => {
  return httpClient<ZoneNodeCountResponse>(
    `/zones/${encodeURIComponent(geoName)}/nodes`,
    HttpMethod.Get
  );
};

export const getZoneServiceCount = (geoName: string): Promise<ZoneServiceCountResponse> => {
  return httpClient<ZoneServiceCountResponse>(
    `/zones/${encodeURIComponent(geoName)}/services`,
    HttpMethod.Get
  );
};

export const getZoneUserCount = (geoName: string): Promise<ZoneUserCountResponse> => {
  return httpClient<ZoneUserCountResponse>(
    `/zones/${encodeURIComponent(geoName)}/users`,
    HttpMethod.Get
  );
};

/** One call for all four counts, so prefer it over the individual count
 *  endpoints whenever a screen needs more than one of them. */
export const getZoneSummary = (geoName: string): Promise<ZoneSummaryResponse> => {
  return httpClient<ZoneSummaryResponse>(
    `/zones/${encodeURIComponent(geoName)}/summary`,
    HttpMethod.Get
  );
};

export const renameZone = (payload: RenameZoneRequest): Promise<RenameZoneResponse> => {
  return httpClient<RenameZoneResponse>('/zones/rename', HttpMethod.Post, payload);
};

/** Nulls and re-derives `structures.node.zone` for the whole system. Require an
 *  explicit user confirmation before calling this. */
export const rebuildNodeZones = (): Promise<RebuildNodeZonesResponse> => {
  return httpClient<RebuildNodeZonesResponse>('/rebuildNodeZones', HttpMethod.Post);
};
